import api from './api'

export interface GoAffProPublicConfig {
  enabled: boolean
  public_token?: string | null
}

export interface GoAffProConversionInput {
  sessionId: string
  email?: string | null
  totalPrice?: number
}

declare global {
  interface Window {
    Goaffpro?: (...args: unknown[]) => void
  }
}

const GOAFFPRO_SDK_URL = 'https://static.goaffpro.com/client_sdk.js'
let sdkLoadPromise: Promise<void> | null = null
let initializedToken: string | null = null

function loadGoAffProSdk(): Promise<void> {
  if (sdkLoadPromise) return sdkLoadPromise

  sdkLoadPromise = new Promise((resolve, reject) => {
    if (window.Goaffpro) {
      resolve()
      return
    }

    const queue: unknown[][] = []
    window.Goaffpro = (...args: unknown[]) => {
      queue.push(args)
    }

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = GOAFFPRO_SDK_URL
    script.onload = () => {
      queue.forEach((item) => window.Goaffpro?.(...item))
      resolve()
    }
    script.onerror = () => reject(new Error('Failed to load GoAffPro SDK'))
    document.head.appendChild(script)
  })

  return sdkLoadPromise
}

export function getAffiliateRefFromBrowser(): string | null {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  const urlRef = params.get('ref')
  if (urlRef?.trim()) return urlRef.trim()

  const match = document.cookie.match(/(?:^|;\s*)ref=([^;]+)/)
  return match ? decodeURIComponent(match[1]).trim() : null
}

class GoAffProService {
  private config: GoAffProPublicConfig | null = null

  async getPublicConfig(): Promise<GoAffProPublicConfig> {
    if (this.config) return this.config
    try {
      const response = await api.get<GoAffProPublicConfig>('/platform-settings/goaffpro-public')
      this.config = {
        enabled: Boolean(response.data?.enabled),
        public_token: response.data?.public_token || null,
      }
      return this.config
    } catch {
      this.config = { enabled: false, public_token: null }
      return this.config
    }
  }

  async initTracking(): Promise<void> {
    const config = await this.getPublicConfig()
    const token = config.public_token?.trim()
    if (!config.enabled || !token) return

    await loadGoAffProSdk()
    if (initializedToken !== token) {
      window.Goaffpro?.('init', token)
      initializedToken = token
    }
    window.Goaffpro?.('track-visit')
  }

  async trackPlusConversion(input: GoAffProConversionInput): Promise<void> {
    const config = await this.getPublicConfig()
    const token = config.public_token?.trim()
    if (!config.enabled || !token) return

    await loadGoAffProSdk()
    if (initializedToken !== token) {
      window.Goaffpro?.('init', token)
      initializedToken = token
    }

    const totalPrice = input.totalPrice ?? 12
    window.Goaffpro?.('track-conversion', {
      id: input.sessionId,
      number: input.sessionId,
      total_price: totalPrice,
      subtotal_price: totalPrice,
      email: input.email || undefined,
    })
  }
}

export const goaffproService = new GoAffProService()
